import * as React from "react";
import {
  List,
  Stack,
  Divider,
  Tooltip,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Popover,
  IconButton,
  Badge,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import RedeemOutlinedIcon from "@mui/icons-material/RedeemOutlined";
import Icon from "../../../meesha_icon.svg";
import { useCookies } from "react-cookie";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Logout,
  Person,
} from "@mui/icons-material";
import { BanknoteArrowDown, BarChart3, BellDot, ChartNoAxesCombined, ClipboardList, FilePen, FileText, House, Inbox, LayoutDashboard, ListChecks, Mail, MessageCircle, Send, Settings, ShieldCheck, Summary, Trophy } from "lucide-react";
import useAccess from "../../../hooks/useAccess";

/*global chrome*/
const ACCENT = "#E8461B";
const ACCENT_SOFT = "rgba(232, 70, 27, 0.10)";
const ICON_SIZE = 20;
const BRAND_GRADIENT = "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)";

// Rounded tile per nav item; the active page gets the same orange gradient pill
// used for selected tabs across the app (Plans, Profile).
const navItemSx = (isActive) => ({
  flexDirection: "column",
  justifyContent: "center",
  gap: 0.5,
  px: 0.25,
  py: 1,
  minHeight: 58,
  borderRadius: "12px",
  color: isActive ? "#fff" : "#6B7280",
  background: isActive ? BRAND_GRADIENT : "transparent",
  boxShadow: isActive ? "0 4px 12px rgba(255,82,0,0.28)" : "none",
  transition: "background-color 150ms ease, color 150ms ease",
  "&:hover": {
    background: isActive ? BRAND_GRADIENT : "#FFF3EC",
    color: isActive ? "#fff" : "#FF5200",
  },
});

const navIconSx = {
  minWidth: 0,
  color: "inherit",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const navLabelSx = (isActive) => ({
  fontSize: 10,
  fontWeight: isActive ? 700 : 600,
  letterSpacing: "0.01em",
  lineHeight: 1.1,
  textAlign: "center",
  width: "100%",
  color: "inherit",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const sidebar_options = [
  {
    icon: (
      <Tooltip title="Smart Dashboard" placement="right" arrow>
        {/* <Dashboard /> */}
        <LayoutDashboard size={ICON_SIZE} />
      </Tooltip>
    ),
    redirect: "/filter/influencers",
    id: "smartdashboard",
    label: "Dashboard"
  },
  {
    icon: (
      <Tooltip title="Reviews Leaderboard" placement="right" arrow>
        <Trophy size={ICON_SIZE} />
      </Tooltip>
    ),
    redirect: "/reviews-leaderboard",
    id: "reviews_leaderboard",
    label: "Leaderboard"
  },
  // {
  //   icon: (
  //     <Tooltip title="History" placement="right" arrow>
  //       {/* <ManageHistory /> */}
  //       <History size={ICON_SIZE} />
  //     </Tooltip>
  //   ),
  //   redirect: "/history",
  //   id: "history"
  // },
  // {
  //   icon: (
  //     <Tooltip title="Profile" placement="right" arrow>
  //       {/* <AccountCircle /> */}
  //       <UserStar size={ICON_SIZE} />
  //     </Tooltip>
  //   ),
  //   redirect: "/profile",
  //   id: "profile"
  // },
  // {
  //   icon: (
  //     <Tooltip title="Referrals" placement="right" arrow>
  //       {/* <RedeemOutlinedIcon /> */}
  //       <Handshake size={ICON_SIZE} />
  //     </Tooltip>
  //   ),
  //   redirect: "/referrals",
  //   id: "referrals"
  // },
   {
    icon: (
      <Tooltip title="Brief" placement="right" arrow>
        <ListChecks size={ICON_SIZE} />
      </Tooltip>
    ),
    redirect: "/brief",
    id: "brief",
    label: "Brief"
  },
  {
    icon: (
      <Tooltip title="Plans & Lists" placement="right" arrow>
        <ClipboardList size={ICON_SIZE} />
      </Tooltip>
    ),
    redirect: "/plans",
    id: "plans",
    label: "Plans"
  },
  {
    icon: (
      <Tooltip title="Script Approval" placement="right" arrow>
        <FilePen size={ICON_SIZE} />
      </Tooltip>
    ),
    redirect: "/scripts",
    id: "scripts",
    label: "Scripts"
  },
  {
    icon: (
      <Tooltip title="Reports" placement="right" arrow>
        <Summary size={ICON_SIZE} />
      </Tooltip>
    ),
    redirect: "/reports",
    id: "reports",
    label: "Reports"
  },
  {
    id: "payments",
    label: "Payments",
    icon: (
      <Tooltip title="Payments" placement="right" arrow>
        <BanknoteArrowDown size={ICON_SIZE} />
      </Tooltip>
    ),
    redirect: "/payments",
    submenu: [
      {
        label: "Home",
        redirect: "/payments",
        icon: <House size={ICON_SIZE} />,
        id: "payments_home"
      },
      {
        label: "Campaigns",
        redirect: "/payments/campaigns",
        icon: <ChartNoAxesCombined size={ICON_SIZE} />,
        id: "payments_campaigns"
      },
      {
        label: "Status",
        redirect: "/payments/status",
        icon: <BellDot size={ICON_SIZE} />,
        id: "payments_status"
      },
    ]
  },
  {
    id: "whatsapp",
    label: "WhatsApp",
    icon: (
      <Tooltip title="WhatsApp" placement="right" arrow>
        <MessageCircle size={ICON_SIZE} />
      </Tooltip>
    ),
    redirect: "/whatsapp/inbox",
    submenu: [
      {
        label: "Inbox",
        redirect: "/whatsapp/inbox",
        icon: <Inbox size={ICON_SIZE} />,
        id: "whatsapp_inbox"
      },
      {
        label: "Templates",
        redirect: "/whatsapp/templates",
        icon: <FileText size={ICON_SIZE} />,
        id: "whatsapp_templates"
      },
      {
        label: "Campaigns",
        redirect: "/whatsapp/campaigns",
        icon: <Send size={ICON_SIZE} />,
        id: "whatsapp_campaigns"
      },
    ]
  },
  {
    id: "email",
    label: "Email",
    icon: (
      <Tooltip title="Email" placement="right" arrow>
        <Mail size={ICON_SIZE} />
      </Tooltip>
    ),
    redirect: "/email/inbox",
    submenu: [
      {
        label: "Inbox",
        redirect: "/email/inbox",
        icon: <Inbox size={ICON_SIZE} />,
        id: "email_inbox"
      },
      {
        label: "Campaigns",
        redirect: "/email/campaigns",
        icon: <Send size={ICON_SIZE} />,
        id: "email_campaigns"
      },
    ]
  },

];

// Not part of `sidebar_options` — only Super Admins may see or reach this section, so it's
// appended conditionally in the component body instead (see `visibleSidebarOptions` below).
const access_management_option = {
  icon: (
    <Tooltip title="Access Management" placement="right" arrow>
      <ShieldCheck size={ICON_SIZE} />
    </Tooltip>
  ),
  redirect: "/access-management",
  id: "access_management",
  label: "Access"
};

// Also not part of `sidebar_options`, for the same reason as access_management_option
// above — the Revenue/Budget/Bank-Details reports must stay super-admin-only regardless
// of any Access Management grant, so it's spliced into the already-computed "payments"
// submenu only when isSuperUser (see visibleSidebarOptions below), never filtered through
// hasPermission().
const payments_reports_submenu_item = {
  label: "Reports",
  redirect: "/payments/reports",
  icon: <BarChart3 size={ICON_SIZE} />,
  id: "payments_reports"
};

const RenderSubmenuPopover = React.memo(({ submenu, isOpen, handleToggle, anchorEl }) => {
  return (
    <>
      <Popover
        open={isOpen}
        onClose={handleToggle}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              ml: 1,
              minWidth: 180,
              borderRadius: 2.5,
              overflow: "hidden",
              boxShadow: "0 12px 32px rgba(20, 20, 20, 0.16)",
              border: "1px solid rgba(0,0,0,0.06)",
            },
          },
        }}
      >
        <List
          sx={{ py: 0.75, px: 0.5 }}
        >
          {
            submenu.map(sm => (
              <ListItem
                key={`submenu_${sm.id}`}
                disablePadding
                sx={{ px: 0.5, py: 0.25 }}
              >
                <ListItemButton
                  component={"a"}
                  href={sm.redirect}
                  target="blank"
                  sx={{
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 0.75,
                    transition: "background-color 120ms ease",
                    "&:hover": {
                      backgroundColor: ACCENT_SOFT,
                    },
                    display: "flex",
                    columnGap: 0.75,
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <ListItemIcon
                    sx={{
                      justifyContent: "center",
                      paddingY: { xs: 1, md: 0 },
                      minWidth: 'auto',
                      height: '100%'
                    }}
                  >
                    {sm.icon}
                  </ListItemIcon>

                  <ListItemText
                    primary={sm.label}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))
          }
        </List>
      </Popover>
    </>
  )
});

function SidebarComponent() {
  const [cookies, removeCookie] = useCookies(["user", "auth"]);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [subMenuAnchorEl, setSubmenuAnchorEl] = React.useState(null);
  const [activeSubmenu, setActiveSubmenu] = React.useState(null);

  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const location = useLocation();
  const id = open ? "simple-popover" : undefined;

  const { isSuperUser, hasPermission } = useAccess();
  // The Access Management tab's "Section access" checklist (helpers/accessControl.js#
  // PERMISSION_CATALOG) only actually restricts anything if the sidebar honors it — a
  // parent (Payments/WhatsApp) with none of its children permitted is dropped entirely
  // rather than left pointing at a page the user can't reach.
  const visibleSidebarOptions = sidebar_options.reduce((acc, option) => {
    if (option.submenu) {
      const visibleSubmenu = option.submenu.filter((item) => hasPermission(item.id));
      if (visibleSubmenu.length) {
        acc.push(
          visibleSubmenu.length === option.submenu.length
            ? option
            : { ...option, submenu: visibleSubmenu, redirect: visibleSubmenu[0].redirect }
        );
      }
    } else if (option.id === "profile" || hasPermission(option.id)) {
      // "profile" has no PERMISSION_CATALOG entry — every role can always reach their own
      // profile, so it's excluded from gating rather than needing to be granted explicitly.
      acc.push(option);
    }
    return acc;
  }, []);
  if (isSuperUser) {
    visibleSidebarOptions.push(access_management_option);
    // Replaces (rather than mutates) the "payments" entry — the reduce above can push the
    // very same object reference that lives in the module-level `sidebar_options` array, so
    // mutating it in place would leak an extra "Reports" item into that shared constant on
    // every remount (Sidebar remounts on every navigation — see the comment below).
    const paymentsIndex = visibleSidebarOptions.findIndex((option) => option.id === "payments");
    if (paymentsIndex !== -1) {
      visibleSidebarOptions[paymentsIndex] = {
        ...visibleSidebarOptions[paymentsIndex],
        submenu: [...visibleSidebarOptions[paymentsIndex].submenu, payments_reports_submenu_item],
      };
    }
  }

  // No queryFn — just reading what WhatsAppNotificationsListener.jsx (mounted once,
  // app-root level) already wrote into this same cache key on a live reply. Sidebar itself
  // remounts on every navigation like every other per-page component, but the react-query
  // cache is a module-level singleton (App.js), so it re-reads the surviving count
  // instantly instead of starting back at zero.
  const { data: whatsappUnreadCount } = useQuery({ queryKey: ["whatsapp-unread"], enabled: false });
  // Same cache-only read pattern as above — written by the same shared
  // NotificationsListener.jsx, this time for brief-assignment pings.
  const { data: briefUnreadCount } = useQuery({ queryKey: ["brief-unread"], enabled: false });
  // Same cache-only read pattern as above — written by the same shared
  // NotificationsListener.jsx, this time for Gmail reply pings.
  const { data: emailUnreadCount } = useQuery({ queryKey: ["email-unread"], enabled: false });
  const unreadCountByOptionId = { whatsapp: whatsappUnreadCount, brief: briefUnreadCount, email: emailUnreadCount };

  const toggleSubmenu = (menu) => {
    setActiveSubmenu(prev =>
      prev === menu ? null : menu
    );
  }

  const closeSubmenu = () => {
    setActiveSubmenu(null);
    setSubmenuAnchorEl(null);
  }

  const handleMenuClick = (event, option) => {
    if (option.submenu) {
      setSubmenuAnchorEl(event.currentTarget);
      setActiveSubmenu(option.id);
      return;
    }

    navigate(option.redirect);
  }

  const handleLogout = (event) => {
    removeCookie("auth", "", {
      path: "/",
      expires: new Date(1691479246),
    });

    removeCookie("auth", "", {
      path: "/filter",
      expires: new Date(1691479246),
    });

    removeCookie("auth", "", {
      path: "/filter/influencers",
      expires: new Date(1691479246),
    });

    removeCookie("user", "", {
      path: "/",
      expires: new Date(1691479246),
    });

    try {
      chrome.runtime.sendMessage(
        process.env.REACT_APP_EXTENSION_ID,
        {
          message: "logout_user",
        },
        function (responseFromExtension) {
          responseFromExtension = responseFromExtension || {};
        }
      );
    } catch (error) {
      console.log(error);
    }
    window.location.href = "/";
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Stack flexGrow={1}>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/"
              disableRipple
              sx={{
                textDecoration: "none",
                margin: "0 auto",
                padding: "10px 0",
                justifyContent: "center",
                borderRadius: 2,
                transition: "transform 150ms ease",
                "&:hover": {
                  backgroundColor: "transparent",
                  transform: "scale(1.06)",
                },
              }}
            >
              <img
                src={Icon}
                style={{
                  width: "30px",
                  height: "30px",
                }}
                alt="icon"
              />
            </ListItemButton>
          </ListItem>

          <Divider sx={{ my: 1, mx: 1.5, borderColor: "#F3F4F6" }} />

          {visibleSidebarOptions.map((option) => {
            const isActive = location.pathname.startsWith(option.redirect);
            return (
            <ListItem
              disablePadding
              key={option.id}
              sx={{ px: 1, py: 0.375 }}
            >
              <ListItemButton
                onClick={(e) => handleMenuClick(e, option)}
                sx={navItemSx(isActive)}
              >
                <ListItemIcon sx={navIconSx}>
                  {unreadCountByOptionId[option.id] > 0 ? (
                    <Badge badgeContent={unreadCountByOptionId[option.id]} max={99} color="error">
                      {option.icon}
                    </Badge>
                  ) : (
                    option.icon
                  )}
                </ListItemIcon>
                <Typography sx={navLabelSx(isActive)}>
                  {option.label}
                </Typography>
              </ListItemButton>

              {/* Show submenu/popover on click */}
              {
                Boolean(option.submenu) ?
                  <RenderSubmenuPopover
                    submenu={option?.submenu}
                    isOpen={activeSubmenu === option.id}
                    handleToggle={() => toggleSubmenu(option.id)}
                    anchorEl={subMenuAnchorEl}
                  />
                  :
                  null
              }
            </ListItem>
            );
          })}
        </List>
      </Stack>

      <Stack>
        <List onClick={handleClick} aria-describedby={id}>
          <ListItem disablePadding sx={{ px: 1, py: 0.375 }}>
            <ListItemButton sx={navItemSx(open)}>
              <ListItemIcon sx={navIconSx}>
                <Tooltip title="Settings" placement="right" arrow>
                  <Settings size={ICON_SIZE} />
                </Tooltip>
              </ListItemIcon>
              <Typography sx={navLabelSx(open)}>
                Settings
              </Typography>
            </ListItemButton>
          </ListItem>
        </List>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  handleClose();
                  navigate("/profile");
                }}
              >
                <ListItemIcon>
                  <Person />
                </ListItemIcon>
                <ListItemText color="#FF5200" primary={cookies.user?.name} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon>
                  <Logout />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </List>
        </Popover>
      </Stack>
    </>
  );
}

export default SidebarComponent;
